<template>
  <div class="custom-form custom-form--login">
    <div class="custom-form__header">
      <slot name="content-introduction"/>
    </div>
    <div class="columns">
      <div class="column --gray">
        <!-- Set form autocomplete to off -->
        <form
          @submit.prevent="submit"
          autocomplete="off"
          novalidate>
          <div class="fieldset">
            <field-input
              v-model="form.email"
              :v="$v.form.email"
              :field="fieldEmail"/>
          </div>
          <div class="fieldset">
            <field-input
              v-model="form.password"
              :v="$v.form.password"
              :field="fieldPassword"/>
          </div>
          <slot name="content-link-forgot-password"/>
          <div class="fieldset">
            <field-checkbox
              v-model="form.rememberCredentials"
              :v="$v.form.rememberCredentials"
              :field="fieldRememberCredentials"/>
          </div>
          <div class="custom-form__actions">

            <!-- CAPTCHA -->
            <captcha
              ref="captcha"
              v-if="fieldCaptcha.isVisible"
              :captcha-id="fieldCaptcha.id"
              :captcha-sitekey="fieldCaptcha.siteKey"
              :explicitly-render="true"
              @captchaError="(value) => this.getCaptchaError(value)"
            >
              <p
                class="field-help"
                v-if="hasErrorCapcha"
                :id="fieldCaptcha.idError">
                {{ fieldCaptcha.error }}
              </p>
            </captcha>
            <!-- / CAPTCHA -->

            <div>
              <button
                type="submit"
                :class="[ctaClass, { 'disabled': $v.form.$invalid }]"
                :disabled="$v.form.$invalid"
              >
                <span class="btn__wrapper">{{ template.ctaSubmit }}</span>
              </button>
            </div>
            <div role="alert">
              <p v-if="serverResponse !== null && serverResponse === false"
                  class="custom-form__response"
                  :class="{ 'error': !serverResponse }">{{ template.failure.default }}</p>
              <p v-if="serverError !== null"
                  class="custom-form__response error">{{ template.error }}</p>
            </div>
          </div>
        </form>
      </div>
      <div class="column">
        <slot name="content-new-account"/>
      </div>
    </div>
  </div>
</template>

<script>
  import { required, email } from 'vuelidate/lib/validators';
  import { tag } from '@Foundation/analyticsHandler';
  import { validationMixin } from 'vuelidate';
  import { postData } from '@Foundation/services';
  import FieldCheckbox from '@Feature/CustomForms/fields/FieldCheckbox/code/Scripts/components/field-checkbox.vue';
  import FieldInput from '@Feature/CustomForms/fields/FieldInput/code/Scripts/components/field-input.vue';
  import Captcha from '@Feature/Captcha/code/Scripts/components/captcha.vue';
  import { AnalyticsHandler } from '@Foundation/analyticsHandler';

  export default {
    name: 'custom-form-login',

    components: { FieldInput, FieldCheckbox, Captcha },

    directives: { tag },

    mixins: [validationMixin],

    props: {
      fieldEmail: { type: Object, required: true, default: undefined },
      fieldPassword: { type: Object, required: true, default: undefined },
      fieldRememberCredentials: { type: Object, required: true, default: undefined },
      fieldCaptcha: { type: Object, required: false, default: undefined },
      template: { type: Object, required: true, default: undefined },
      apiUrl: { type: String, required: true, default: undefined },
      ctaClass: {type: String, default: 'btn btn--black'}
    },

    data() {
      return {
        form: {
          email: '',
          password: '',
          rememberCredentials: false,
        },
        serverResponse: null,
        serverError: null,
        counter: 0,
        hasErrorCapcha: false
      };
    },

    /**
     * Vuelidate config
     * @see {@link https://github.com/monterail/vuelidate|GitHub}
     */
    validations: {
      form: {
        email: {
          required,
          email
        },
        password: {
          required
        },
        rememberCredentials: {},
      }
    },

    methods: {
      getCaptchaError(val) {
        this.hasErrorCapcha = val;
      },

      /** check if captcha required for validation before sending a request
       * @param {function} callback - function to call after validation
       */
      validateCapcha(callback) {
        if (this.fieldCaptcha.isVisible) {
          this.$refs.captcha.checkCaptcha();

          this.$nextTick(() => {
            /* istanbul ignore else */
            if (!this.hasErrorCapcha) {
              callback();
            }
          });

        } else {
          callback();
        }
      },

      pushTagging(label) {
        AnalyticsHandler.pushDataLayer({
          'event': 'uaevent',
          'ecommerce': 'undefined',
          'eventCategory': 'login',
          'eventAction': 'regular',
          'eventLabel': label,
          'event_name': 'login',
          'method': 'regular',
          'referral': 'my account'
        });
      },

      sendRequest() {
        postData(`${window.location.origin}/${this.apiUrl}`, this.form)
          .then((response) => {
            this.serverResponse = response.status;
            this.counter += 1;
            const taggingLabel = response.status ? 'my account' : 'error';
           // this.pushTagging(taggingLabel);
            /* istanbul ignore else */
            if (!!response.url && response.status) {
              this.pushTagging(taggingLabel);
              window.location.replace(response.url);
            }
            /* istanbul ignore else */
            if (!!response.autoRedirect && this.counter > 4) {
              
              window.location.replace(response.autoRedirect);
            }
          })
          .catch(() => {
            this.serverError = true;
          });
      },

      submit() {
        this.$v.form.$touch();
        if (this.$v.form.$pending || this.$v.form.$error) {
          return;
        } else {
          this.serverResponse = null;

          this.validateCapcha(this.sendRequest);
        }
      },
    }
  };
</script>

<style lang='scss' src="./custom-form-login.scss"></style>
